<template>
  <content-dialog
    :visible.sync="modalVisible"
    max-width="500px"
  >
    <!--<editor-fold desc="Dialog Title">-->
    <template slot="dialogTitle">
      <span v-if="slotDirty.id">
        Edit Yacht Sales Slot
      </span>
      <span v-else>
        Add New Yacht Sales Slot
      </span>
    </template>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Content">-->
    <v-card-text>
      <validation-observer ref="validationObserver">
        <v-autocomplete
          v-model="slotDirty.yacht_manufacturer_id"
          :items="yachtManufacturers.data"
          :loading="yachtManufacturers.loading"
          label="Yacht Manufacturer"
          item-text="name"
          item-value="id"
          autocomplete="off"
          outlined
          :disabled="!!slotDirty.id"
        />

        <validation-provider
          v-slot="{ errors }"
          rules="required"
        >
          <v-text-field
            v-model="slotDirty.yacht_model"
            label="Yacht Model"
            outlined
            clearable
            :error-messages="errors"
            hint="Enter the yacht model the yard slot is for"
          />
        </validation-provider>

        <v-text-field
          v-model="slotDirty.yard_id"
          label="Yard Slot ID (identification)"
          outlined
          clearable
          hint="Enter slot identification from yard for subsequent cross-referencing"
        />

        <v-select
          v-if="slotDirty.id"
          v-model="slotDirty.status"
          :items="allStatuses"
          item-value="value"
          item-text="text"
          outlined
          label="Sales Slot Status"
        />

        <v-menu
          v-model="exFactoryDatePickerVisible"
          offset-y
          min-width="auto"
          :close-on-content-click="false"
        >
          <template v-slot:activator="{ on, attrs }">
            <validation-provider
              v-slot="{ errors }"
              rules="required"
              name="Ex Factory Date"
            >
              <v-text-field
                :value="slotDirty.ex_factory_date ? $moment(slotDirty.ex_factory_date).format('DD.MM.YYYY.') : ''"
                :error-messages="errors"
                label="Ex factory date"
                outlined
                clearable
                readonly
                v-bind="attrs"
                v-on="on"
              />
            </validation-provider>
          </template>
          <v-date-picker
            v-model="slotDirty.ex_factory_date"
            @change="exFactoryDatePickerVisible = false"
          />
        </v-menu>

        <v-menu
          v-model="orderDatePickerVisible"
          offset-y
          min-width="auto"
          :close-on-content-click="false"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              :value="slotDirty.last_order_date ? $moment(slotDirty.last_order_date).format('DD.MM.YYYY.') : ''"
              label="Last order / production date"
              outlined
              clearable
              readonly
              v-bind="attrs"
              v-on="on"
            />
          </template>
          <v-date-picker
            v-model="slotDirty.last_order_date"
            @change="orderDatePickerVisible = false"
          />
        </v-menu>

        <v-textarea
          v-model="slotDirty.note"
          outlined
          label="Note"
          placeholder="e.g. Mediterranean, 110V"
          rows="3"
        />

        <validation-provider
          v-if="!salesSlot.id"
          v-slot="{ errors }"
          rules="required"
        >
          <v-text-field
            v-model="timesToAdd"
            type="number"
            label="How many of these stock slots?"
            :error-messages="errors"
            outlined
          />
        </validation-provider>

        <v-switch
          v-model="slotDirty.partners_visible"
          label="Visible to partner sales?"
        />
      </validation-observer>
    </v-card-text>
    <!--</editor-fold>-->

    <!--<editor-fold desc="Dialog Actions">-->
    <template slot="dialogActions">
      <v-tooltip top>
        <template v-slot:activator="{ on }">
          <span v-on="salesSlot.yachts_count > 0 || salesSlot.yachts.length > 0 ? on : undefined">
            <v-btn
              v-if="$can('delete', 'YachtSalesSlot') && slotDirty.id"
              small
              outlined
              color="error"
              :disabled="salesSlot.yachts_count > 0 || salesSlot.yachts.length > 0"
              @click="toggleConfirmationDialog(true)"
            >
              <v-icon
                left
                small
              >
                delete
              </v-icon>
              Delete
            </v-btn>
          </span>
        </template>
        <span>
          Not possible since there are yachts connected to this sales slot.
        </span>
      </v-tooltip>
      <v-spacer />
      <v-btn
        text
        color="grey"
        @click="modalVisible = false"
      >
        Cancel
      </v-btn>
      <v-btn
        color="primary"
        :loading="submitting"
        @click="submit"
      >
        <v-icon
          small
          left
        >
          check
        </v-icon>
        Save
      </v-btn>
    </template>
    <!--</editor-fold>-->

    <confirm-delete-dialog
      :visible.sync="deleteConfirmationDialogVisible"
      :submitting="deleting"
      text="Are you sure you want to delete this yacht sales slot?"
      @confirmed="deleteSlot()"
    />
    <!--</editor-fold>-->
  </content-dialog>
</template>

<script>
import { cloneDeep, debounce } from 'lodash'
import YachtModel from '@/models/YachtModel'
import deleteConfirmationMixin from '@/mixins/deleteConfirmationMixin'
import ContentDialog from '@/components/helpers/Dialog'
import ConfirmDeleteDialog from '@/components/helpers/ConfirmDeleteDialog'
import { mapGetters } from 'vuex'
import YachtSalesSlot from '@/models/YachtSalesSlot'
import validationScrollMixin from '@/mixins/validationScrollMixin'

export default {
  name: 'YachtSalesSlotEditDialog',
  components: {
    ContentDialog,
    ConfirmDeleteDialog
  },
  mixins: [deleteConfirmationMixin, validationScrollMixin],
  props: {
    visible: Boolean,
    salesSlot: {
      type: Object,
      default: () => YachtSalesSlot.emptyModel
    }
  },
  data() {
    return {
      slotDirty: cloneDeep(this.salesSlot),
      yachtManufacturerId: null,
      timesToAdd: 1,
      submitting: false,
      yachtModels: {
        loading: false,
        data: []
      },
      exFactoryDatePickerVisible: false,
      orderDatePickerVisible: false
    }
  },
  computed: {
    modalVisible: {
      get: function () {
        return this.visible
      },
      set: function (value) {
        this.$emit('update:visible', value)
      }
    },
    ...mapGetters('settings', ['yachtManufacturers'])
  },
  watch: {
    salesSlot: function () {
      this.slotDirty = cloneDeep(this.salesSlot)
    },
    yachtManufacturerId: function () {
      this.fetchYachtModels()
    }
  },
  created() {
    this.fetchYachtModels()

    this.allStatuses = YachtSalesSlot.allStatuses()
  },
  methods: {
    async submit() {
      let validation = await this.$refs.validationObserver.validate()

      if (!validation) {
        this.showAlertAndScrollToFirstError()
        return
      }

      this.saveSlot()
    },
    async saveSlot() {
      this.submitting = true

      try {
        let formData = this.slotDirty

        if (!this.slotDirty.id) {
          formData = {
            ...formData,
            times: this.timesToAdd
          }
        }

        let salesSlot = await YachtSalesSlot.api.save(formData, this.slotDirty.id)

        this.$store.dispatch('alert', {
          'text': 'Yacht sales stock successfully saved',
          'color': 'success'
        })
        this.$emit('update:sales-slot', salesSlot)
        this.modalVisible = false
      } catch (e) {
        if (e.response && e.response.status === 422) {
          this.$store.dispatch('alert', {
            'text': e.response.data.message,
            'color': 'warning'
          })
        } else {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
        }

        throw e
      } finally {
        this.submitting = false
      }
    },
    async deleteSlot() {
      this.deleting = true

      try {
        await YachtSalesSlot.api.destroy(this.slotDirty.id)

        this.$store.dispatch('alert', {
          'text': 'Sales stock slot deleted',
          'color': 'info'
        })
        this.modalVisible = false

        this.$emit('deleted:sales-slot')
      } catch (e) {
        if (e.response && e.response.status === 403) {
          this.$store.dispatch('alert', {
            'text': 'Insufficient privileges.',
            'color': 'error'
          })
        } else {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong.',
            'color': 'error'
          })
        }

        throw e
      } finally {
        this.deleteConfirmationDialogVisible = false
        this.deleting = false
      }
    },
    fetchYachtModels: debounce(async function () {
      const query = {
        yacht_manufacturer_id: this.yachtManufacturerId ? this.yachtManufacturerId : undefined,
        status: YachtModel.STATUS_ACTIVE,
        order_by: 'label',
        is_sales_active: true
      }

      try {
        this.yachtModels.data = await YachtModel.api.fetch(query)
      } catch (e) {
        throw e
      } finally {
        this.yachtModels.loading = false
      }
    }, 500)
  }
}
</script>
